// Friendly names for the AWS accounts on a customer record (ENTRAID-294), so
// a person reads "Example Corp (123456789012)" instead of a bare number.
//
// Two sources per account:
//   - awsAccountAlias: the IAM account alias, resolved through the trust role
//     with iam:ListAccountAliases and cached on the record by the fleet-health
//     cycle (lib/account-alias.mjs). Deployment account only: the portal has
//     no role in the Marketplace subscriber account.
//   - awsAccountName / marketplaceAccountName: a friendly name typed by an
//     operator or the customer admin.
//
// Display rule: the alias when present, else the stored name, else nothing.
// The 12-digit id is always shown. Pure functions, no AWS imports: used by
// the portal API and the SPA.

export const ACCOUNT_NAME_MAX = 64;

// No control characters or line breaks, and no angle brackets: the value is
// rendered next to an id and stays plain text everywhere.
const CONTROL_CHARS_RE = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/;
const MARKUP_CHARS_RE = /[<>]/;

/**
 * Validates a stored friendly account name.
 * Returns { ok: true, value } with the trimmed value, { ok: true, value: null }
 * for null or an empty string (clears the name), or { ok: false, error }.
 */
export function normalizeAccountName(input) {
  if (input === null || input === undefined) return { ok: true, value: null };
  if (typeof input !== 'string') return { ok: false, error: 'must be text' };
  const value = input.trim();
  if (value === '') return { ok: true, value: null };
  if (value.length > ACCOUNT_NAME_MAX) return { ok: false, error: `must be at most ${ACCOUNT_NAME_MAX} characters` };
  if (CONTROL_CHARS_RE.test(value)) return { ok: false, error: 'must not contain control characters or line breaks' };
  if (MARKUP_CHARS_RE.test(value)) return { ok: false, error: 'must not contain < or >' };
  return { ok: true, value };
}

function clean(value) {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/** The name to show next to the customer's AWS (deployment) account id, or null. */
export function awsAccountDisplayName(customer) {
  return clean(customer?.awsAccountAlias) ?? clean(customer?.awsAccountName);
}

/**
 * The name to show next to the Marketplace subscription account id, or null.
 * Its own stored name; when the subscriber account is the deployment account
 * itself, the deployment account's name applies (alias included).
 */
export function marketplaceAccountDisplayName(customer, { sameAsDeployment = false } = {}) {
  return clean(customer?.marketplaceAccountName) ?? (sameAsDeployment ? awsAccountDisplayName(customer) : null);
}

/** "Name (id)" when there is a name, else the bare id; '--' without an id. */
export function accountLabel(name, accountId) {
  if (!accountId) return '--';
  return name ? `${name} (${accountId})` : accountId;
}
